import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Loader2, Repeat, Calendar, Building2, Copy, Check } from "lucide-react";

const AMOUNTS = [500, 1500, 5000, 15000];

export default function Donate({ api, programs = [] }) {
  const [frequency, setFrequency] = useState("one_time"); // one_time | monthly
  const [amount, setAmount] = useState(1500);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    program: "",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [copiedKey, setCopiedKey] = useState("");

  const copy = (key, value) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1600);
    }
  };

  const final = custom ? Number(custom) : amount;
  const isMonthly = frequency === "monthly";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !final || final <= 0) {
      toast.error("Please complete name, email and amount.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${api}/donations`, {
        full_name: form.full_name,
        email: form.email,
        amount: final,
        currency: "INR",
        frequency,
        program: form.program || null,
        note: form.note || null,
      });
      toast.success(
        isMonthly ? "Thank you — monthly pledge received." : "Thank you — pledge received.",
        {
          description: isMonthly
            ? `We'll email auto-debit instructions and your 80G receipt shortly. ₹${final.toLocaleString()}/month, starting next billing cycle.`
            : "We'll email payment instructions and your 80G receipt shortly.",
        }
      );
      setForm({ full_name: "", email: "", program: "", note: "" });
      setCustom("");
      setAmount(1500);
      setFrequency("one_time");
    } catch (err) {
      toast.error("Couldn't record your pledge. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="donate"
      data-testid="donate-section"
      className="relative py-20 md:py-32 bg-cream overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Left copy */}
          <div className="md:col-span-5">
            <p className="overline mb-6">Give</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl text-ink leading-tight text-balance">
              Your <em className="italic text-terracotta">₹1,500</em> sends one child to school for a term.
            </h2>
            <p className="mt-6 text-graphite md:text-lg leading-relaxed">
              94% of every rupee reaches programs. All donations are eligible
              for 80G tax exemption under Indian law. International donors —
              please email us at garajairamadas@gmail.com.
            </p>

            <div
              className="mt-8 bg-sand border-l-2 border-mustard pl-5 py-3"
              data-testid="monthly-callout"
            >
              <p className="overline mb-2 text-terracotta">Why monthly?</p>
              <p className="text-sm text-graphite leading-relaxed">
                Monthly givers fund roughly <span className="text-ink font-medium">5–7×</span> more
                impact over a lifetime — and let us plan programs with confidence
                instead of chasing one-off cheques.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { v: "₹500", l: "School supplies, 1 child" },
                { v: "₹5K", l: "Skilling cohort seat" },
                { v: "₹50K", l: "Funds a ward for a month" },
              ].map((b) => (
                <div
                  key={b.v}
                  className="border-l-2 border-mustard pl-4"
                >
                  <p className="font-serif text-2xl text-ink leading-none">
                    {b.v}
                  </p>
                  <p className="text-xs text-graphite mt-2 leading-snug">
                    {b.l}
                  </p>
                </div>
              ))}
            </div>

            {/* Bank details card */}
            <div
              className="mt-10 bg-forest text-cream p-6 md:p-7"
              data-testid="bank-details"
            >
              <div className="flex items-center gap-3 mb-5">
                <Building2 size={18} strokeWidth={1.5} className="text-mustard" />
                <p className="overline text-mustard">
                  Direct bank transfer
                </p>
              </div>

              <div className="space-y-4 text-sm">
                <BankRow
                  label="Account name"
                  value="Jharkhand Rastriya Sarva Dharma Welfare Association"
                  copyKey="name"
                  copiedKey={copiedKey}
                  onCopy={copy}
                />
                <BankRow
                  label="Bank"
                  value="Bandhan Bank — Sonari Branch, Jamshedpur"
                  copyKey="bank"
                  copiedKey={copiedKey}
                  onCopy={copy}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <BankRow
                    label="Account number"
                    value="20200148146236"
                    mono
                    copyKey="acc"
                    copiedKey={copiedKey}
                    onCopy={copy}
                  />
                  <BankRow
                    label="IFSC code"
                    value="BDBL0002500"
                    mono
                    copyKey="ifsc"
                    copiedKey={copiedKey}
                    onCopy={copy}
                  />
                </div>
              </div>

              <p className="text-xs text-cream/65 mt-6 leading-relaxed">
                After transferring, please share the transaction reference on
                the form on the right (or by email) so we can send your 80G
                receipt.
              </p>
            </div>
          </div>

          {/* Right donation form */}
          <div className="md:col-span-7">
            <form
              onSubmit={submit}
              data-testid="donate-form"
              className="bg-sand border border-line p-7 md:p-10"
            >
              {/* Frequency toggle */}
              <p className="overline mb-3">Give as</p>
              <div
                className="grid grid-cols-2 gap-0 mb-6 border border-line"
                role="tablist"
                data-testid="donate-frequency-toggle"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isMonthly}
                  onClick={() => setFrequency("one_time")}
                  data-testid="donate-frequency-onetime"
                  className={`flex items-center justify-center gap-2 py-3.5 text-sm transition-colors ${
                    !isMonthly
                      ? "bg-forest text-cream"
                      : "bg-cream text-graphite hover:text-ink"
                  }`}
                >
                  <Calendar size={15} strokeWidth={1.5} />
                  One-time
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isMonthly}
                  onClick={() => setFrequency("monthly")}
                  data-testid="donate-frequency-monthly"
                  className={`relative flex items-center justify-center gap-2 py-3.5 text-sm transition-colors ${
                    isMonthly
                      ? "bg-terracotta text-white"
                      : "bg-cream text-graphite hover:text-ink"
                  }`}
                >
                  <Repeat size={15} strokeWidth={1.5} />
                  Monthly
                  {!isMonthly && (
                    <span className="absolute -top-2 right-2 bg-mustard text-ink text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 font-medium">
                      5–7× impact
                    </span>
                  )}
                </button>
              </div>

              <p className="overline mb-3">
                Choose an amount {isMonthly ? "(INR / month)" : "(INR)"}
              </p>
              <div className="grid grid-cols-4 gap-3 mb-4">
                {AMOUNTS.map((a) => {
                  const active = !custom && amount === a;
                  return (
                    <button
                      type="button"
                      key={a}
                      onClick={() => {
                        setAmount(a);
                        setCustom("");
                      }}
                      data-testid={`donate-amount-${a}`}
                      className={`py-4 border transition-colors ${
                        active
                          ? "bg-terracotta border-terracotta text-white"
                          : "bg-cream border-line text-ink hover:border-terracotta hover:text-terracotta"
                      }`}
                    >
                      ₹{a.toLocaleString()}
                      {isMonthly && (
                        <span className="block text-[10px] opacity-75 mt-0.5">
                          /month
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-stretch gap-3 mb-6">
                <span className="inline-flex items-center justify-center bg-forest text-cream px-4 font-serif text-lg">
                  ₹
                </span>
                <input
                  type="number"
                  min="1"
                  placeholder={
                    isMonthly
                      ? "Or enter a custom monthly amount"
                      : "Or enter a custom amount"
                  }
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  data-testid="donate-input-custom"
                  className="flex-1 bg-cream border border-line px-4 py-3 focus:outline-none focus:border-terracotta"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Full name"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  required
                  data-testid="donate-input-name"
                  className="bg-cream border border-line px-4 py-3 focus:outline-none focus:border-terracotta"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="donate-input-email"
                  className="bg-cream border border-line px-4 py-3 focus:outline-none focus:border-terracotta"
                />
                <select
                  value={form.program}
                  onChange={(e) => setForm({ ...form, program: e.target.value })}
                  data-testid="donate-select-program"
                  className="md:col-span-2 bg-cream border border-line px-4 py-3 focus:outline-none focus:border-terracotta"
                >
                  <option value="">Direct to where it's needed most</option>
                  {programs.map((p) => (
                    <option key={p.id} value={p.title}>
                      {p.title}
                    </option>
                  ))}
                </select>
                <textarea
                  rows={2}
                  placeholder="Add a note (optional)"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  data-testid="donate-input-note"
                  className="md:col-span-2 bg-cream border border-line px-4 py-3 focus:outline-none focus:border-terracotta resize-none"
                />
              </div>

              <div className="mt-6 flex items-center justify-between gap-4">
                <p className="text-xs text-graphite max-w-xs leading-relaxed">
                  {isMonthly
                    ? "We'll email you auto-debit instructions (UPI AutoPay / NACH). Cancel anytime."
                    : "This pledge will be confirmed via email with bank/UPI details for completion."}
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="donate-submit"
                  className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-7 py-3.5 transition-colors disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Processing
                    </>
                  ) : (
                    <>
                      Give ₹{(final || 0).toLocaleString()}
                      {isMonthly && (
                        <span className="text-sm opacity-90">/month</span>
                      )}
                      <Heart size={16} strokeWidth={1.8} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function BankRow({ label, value, mono = false, copyKey, copiedKey, onCopy }) {
  const isCopied = copiedKey === copyKey;
  return (
    <div>
      <p className="overline text-mustard mb-1 text-[10px]">{label}</p>
      <div className="flex items-start justify-between gap-3">
        <p
          className={`text-cream leading-snug break-words ${
            mono ? "font-mono tracking-wide" : ""
          }`}
        >
          {value}
        </p>
        <button
          type="button"
          onClick={() => onCopy(copyKey, value)}
          aria-label={`Copy ${label}`}
          data-testid={`bank-copy-${copyKey}`}
          className="flex-shrink-0 p-1.5 border border-cream/25 text-cream/70 hover:text-mustard hover:border-mustard transition-colors"
        >
          {isCopied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      </div>
    </div>
  );
}